import { commentLabelFor } from '@flowx/core-sdk'
import type {
  HarnessAttachmentStatus,
  HarnessSentFile,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import {
  HarnessAdjustmentLevel,
  HarnessPlanAdjustment,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  ADJUSTMENT_NOTICE,
  BRANCH_READY_NOTICE,
  HarnessAnswerInput,
  HarnessRunFold,
  HarnessRunState,
  HarnessSessionState,
  HarnessStoryRunEntry,
  HarnessStreamState,
  HarnessToolStatus,
  HarnessTranscriptEntry,
  HarnessTurnComment,
  INTERRUPTED_NOTICE,
  isPlanProposalSettled,
} from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import {
  HARNESS_ERROR_CODES,
  HarnessBranchHandoff,
  HarnessExecutionPreview,
  HarnessFrame,
  HarnessInterrupt,
  HarnessRunProgressFrame,
  HarnessSessionRecord,
  HarnessStoryFrame,
  HarnessStoryState,
  isPlanReviewType,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import {
  toHistoryFiles,
  withAttachmentStatus,
  withSettledFiles,
} from '@flowx/designer/modules/ai-builder/utils/conversation-files.util'
import { frameFromEvent } from '@flowx/designer/modules/ai-builder/utils/harness-frames.util'
import { toQuestionDisplay } from '@flowx/designer/modules/ai-builder/utils/interrupt-display.util'
import {
  asNumber,
  asString,
  compactJson,
  isRecord,
  tryParseJson,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'
import {
  planUsesStories,
  toStoryPlanSnapshot,
} from '@flowx/designer/modules/ai-builder/utils/story-plan/story-plan-contract.util'

/**
 * How one turn's frames become something renderable. Pure and Angular-free so every awkward
 * transition — a revised plan, a recoverable tool failure, a stream that stops early — is a plain
 * unit test. Time comes in as an argument for the same reason: the clock is the caller's.
 */

export const initialHarnessSessionState: HarnessSessionState = {
  storyPlanSnapshot: null,
  storyPlanProposal: null,
  storyRunEntries: [],
  runEventKeys: [],
  sessionId: null,
  transcript: [],
  runningTools: [],
  streamState: 'idle',
  interrupt: null,
  planRevision: 0,
  latestPlan: null,
  briefRevision: 0,
  latestBrief: null,
  briefRevisionPending: false,
  briefRevisedOnNote: false,
  interruptRecovered: false,
  questionsAnswered: 0,
  record: null,
}

export function applyFrame(
  state: HarnessSessionState,
  frame: HarnessFrame,
  at: number = Date.now()
): HarnessSessionState {
  switch (frame.type) {
    case 'story_started':
    case 'story_implemented':
      return applyStoryFrame(state, frame)

    case 'run_started':
    case 'run_paused':
    case 'run_finished':
    case 'story_needs_you': {
      const key = runEventKey(frame)

      return state.runEventKeys.includes(key)
        ? state
        : recordRunEvent(withStoryNeedingYou(state, frame), key, frame.runId)
    }

    case 'token':
      return { ...state, transcript: appendToken(state.transcript, frame.text, at) }

    case 'tool_use_start': {
      const name = frame.label || frame.name

      return {
        ...state,
        runningTools: [...state.runningTools, { toolUseId: frame.toolUseId, name }],
        transcript: [
          ...state.transcript,
          {
            type: 'tool',
            toolUseId: frame.toolUseId,
            name,
            isRawName: !frame.label,
            status: 'running',
            at,
          },
        ],
      }
    }

    case 'tool_use_result':
      return {
        ...state,
        runningTools: removeRunningTool(state.runningTools, frame.toolUseId),
        transcript: resolveTool(state.transcript, frame, at),
      }

    case 'interrupt':
      return applyInterrupt({ ...state, interruptRecovered: false }, frame.interrupt, at)

    // Not an error — the request was understood and the model tried — and not an interrupt either:
    // nothing approvable was sent. The turn ends `paused: false`, and `endTurn` keeps `blocked`.
    case 'planning_failed':
      return {
        ...settleInterrupt(state),
        streamState: 'blocked',
        transcript: [
          ...settleStreaming(state.transcript),
          {
            type: 'notice',
            level: 'error',
            code: frame.code,
            text: frame.reason,
            ...(frame.hardFailures.length && { details: frame.hardFailures }),
            ...(frame.recoveryAction && { action: frame.recoveryAction }),
          },
        ],
      }

    // The record, and nothing else. Restoring the session from it would rebuild the transcript
    // out of `record.events` — which a run's stream sends and a turn's strips — so one frame type
    // would mean two things, and a record without history would empty the conversation without
    // saying so.
    //
    // A record older than the one held is a late frame, not a rollback: the run's stream and a
    // recovery read can both deliver one, and the newer revision wins whichever arrives second.
    case 'lifecycle':
      return reconcileSessionRecord(state, frame.record)

    // Where the maker goes next is the store's business; the transcript keeps the line so the
    // conversation that asked for the run says what came of it; the session id stays the same.
    case 'branch_ready':
      return withNotice(
        state,
        'info',
        `Implemented and committed on branch ${frame.handoff.branch}.`,
        { code: BRANCH_READY_NOTICE }
      )

    // The plan adjusted itself at a checkpoint of its run, or asks the maker to (D15). Said in the
    // conversation with its level, as the design asks; a repair (L0) changes nothing the maker sees.
    case 'plan_adjusted': {
      const notice = adjustmentNotice(frame.adjustment)

      return notice
        ? { ...state, transcript: [...settleStreaming(state.transcript), notice] }
        : state
    }

    // The harness's own commit moved the conversation's write target on. The record is where the
    // target lives, so it is written there — the next `lifecycle` frame says the same thing.
    case 'target_advanced':
      return state.record
        ? {
            ...state,
            record: { ...state.record, write_target: frame.advanced.write_target },
          }
        : state

    // One of the turn's files moved on. Only the message that carried it — the last one the maker
    // sent — is written: the frames come before any token of the answer to it.
    case 'attachment':
      return { ...state, transcript: withLastTurnAttachment(state.transcript, frame.status) }

    case 'error':
      return withNotice(state, 'error', errorText(frame.code, frame.message), {
        code: frame.code,
        retryable: frame.retryable,
      })

    // No gate survives it: a cancelled resume has already consumed its answer, so the old decision
    // is never offered again — the way on is a new turn. Live, `end` follows and keeps this state.
    case 'interrupted':
      return withNotice(
        {
          ...state,
          runningTools: [],
          streamState: 'interrupted',
          interrupt: null,
          transcript: settleStreaming(state.transcript),
        },
        'info',
        frame.message,
        { code: INTERRUPTED_NOTICE }
      )

    // The contract guarantees `end` is the last frame, so this is where a turn settles.
    case 'end':
      return endTurn(state, frame.paused)

    default:
      return state
  }
}

/** Owner reads and lifecycle frames share the same canonical snapshot reconciliation. */
export function reconcileSessionRecord(
  state: HarnessSessionState,
  record: HarnessSessionRecord,
  acceptActionResponse = false
): HarnessSessionState {
  if (
    (state.sessionId && record.session_id !== state.sessionId) ||
    (!acceptActionResponse && record.revision < (state.record?.revision ?? 0))
  ) {
    return state
  }

  const plan = record.plan
  const proposal = state.storyPlanProposal
  const reviewingProposal = proposal && state.interrupt && isPlanReviewType(state.interrupt.type)
  const proposalNotSettled = reviewingProposal && !isPlanProposalSettled(record, proposal)
  const olderPlanVersion =
    plan?.plan_id === state.record?.plan?.plan_id &&
    plan !== null &&
    plan.version < (state.record?.plan?.version ?? 0)

  if (proposalNotSettled || olderPlanVersion) {
    return { ...state, record }
  }

  // The plan owns decisions as well as stories. A run is a whole-snapshot alternative only
  // when the plan supplied none and its version matches; never a source of missing fields.
  const execution = plan?.execution
  const runSnapshot =
    execution?.type === 'plan' && execution.plan_version === plan?.version
      ? execution.storyPlan
      : undefined
  // A proposal without a plan_id is the plan on screen, as `isPlanProposalSettled` reads it.
  const snapshotPlanId = proposal?.planId ?? state.record?.plan?.plan_id
  const snapshot =
    plan?.storyPlan ??
    runSnapshot ??
    (plan && state.storyPlanSnapshot && plan.plan_id === snapshotPlanId
      ? toStoryPlanSnapshot({ expansion: 'milestones' })
      : null)
  const runId = execution?.run_id
  // On resume the harness can mark its last closed story as implementing until the next
  // story opens. Keep that run's observed completion, not stale starts; the raw record and
  // explicit questions/stops remain authoritative. Never borrow from another plan or run.
  const reconciledSnapshot =
    snapshot?.stories.availability === 'available' &&
    plan?.plan_id === state.record?.plan?.plan_id &&
    runId === state.record?.plan?.execution?.run_id
      ? {
          ...snapshot,
          stories: {
            ...snapshot.stories,
            entries: snapshot.stories.entries.map((story) => {
              const implemented = state.storyRunEntries.find(
                (entry) =>
                  entry.runId === runId &&
                  entry.storyId === story.story_id &&
                  entry.state === 'implemented'
              )
              return implemented &&
                (story.state === 'not_started' || story.state === 'implementing')
                ? {
                    ...story,
                    state: implemented.state,
                    commit_id: implemented.commitId,
                    forecast: false,
                  }
                : story
            }),
          },
        }
      : snapshot
  const entries =
    runId && reconciledSnapshot?.stories.availability === 'available'
      ? [
          ...state.storyRunEntries.filter((entry) => entry.runId !== runId),
          ...reconciledSnapshot.stories.entries
            .filter((story) => story.state !== 'not_started')
            .map((story): HarnessStoryRunEntry => ({
              runId,
              storyId: story.story_id,
              name: story.name,
              state: story.state,
              commitId: story.commit_id,
            })),
        ]
      : state.storyRunEntries

  return {
    ...state,
    record,
    storyPlanSnapshot: reconciledSnapshot,
    storyPlanProposal: null,
    storyRunEntries: entries,
  }
}

/** A run event's identity: its id is only unique within the run. */
function runEventKey(frame: { runId: string; eventId: string }): string {
  return JSON.stringify([frame.runId, frame.eventId])
}

/** A run event brings the run's progress up to date. In place, while it is the same run and the
 * maker has said nothing since its last message; in a new one below otherwise, so the last keeps
 * what they saw — a restarted run is a new run, and the old one keeps its own progress. */
function recordRunEvent(
  state: HarnessSessionState,
  key: string,
  runId: string
): HarnessSessionState {
  const progress: HarnessTranscriptEntry = {
    type: 'run',
    runId,
    stories: state.storyRunEntries.filter((entry) => entry.runId === runId),
  }
  const last = state.transcript.findLastIndex((line) => line.type === 'run')
  const lastRun = state.transcript[last]
  const opensNew =
    lastRun?.type !== 'run' ||
    lastRun.runId !== runId ||
    state.transcript.slice(last + 1).some((line) => line.type === 'user' || line.type === 'answer')

  return {
    ...state,
    transcript: opensNew
      ? [...state.transcript, progress]
      : state.transcript.map((line, index) => (index === last ? progress : line)),
    runEventKeys: [...state.runEventKeys, key],
  }
}

/** A story that stopped for the maker says so in the run message, not that it is still being built.
 * One already implemented stays implemented: a replay never takes a story back. */
function withStoryNeedingYou(
  state: HarnessSessionState,
  frame: HarnessRunProgressFrame
): HarnessSessionState {
  return frame.type === 'story_needs_you' && frame.storyId
    ? {
        ...state,
        storyRunEntries: state.storyRunEntries.map((entry) =>
          entry.runId === frame.runId &&
          entry.storyId === frame.storyId &&
          entry.state !== 'implemented'
            ? { ...entry, state: 'needs_you' }
            : entry
        ),
      }
    : state
}

const STORY_EVENT_STATE: Readonly<Record<HarnessStoryFrame['type'], HarnessStoryState>> = {
  story_started: 'implementing',
  story_implemented: 'implemented',
}

function applyStoryFrame(
  state: HarnessSessionState,
  frame: HarnessStoryFrame
): HarnessSessionState {
  const key = runEventKey(frame)
  if (state.runEventKeys.includes(key)) {
    return state
  }

  const previous = state.storyRunEntries.find(
    (entry) => entry.runId === frame.runId && entry.storyId === frame.storyId
  )
  // Reconnects can replay start after completion. A row never regresses on event order.
  const entry: HarnessStoryRunEntry =
    previous && (previous.state === 'implemented' || frame.type === 'story_started')
      ? { ...previous, commitId: previous.commitId ?? frame.commitId }
      : {
          runId: frame.runId,
          storyId: frame.storyId,
          name: frame.name,
          state: STORY_EVENT_STATE[frame.type],
          commitId: frame.commitId,
        }
  const snapshot = state.storyPlanSnapshot
  const ownRun = state.record?.plan?.execution?.run_id === frame.runId
  return recordRunEvent(
    {
      ...state,
      storyRunEntries: previous
        ? state.storyRunEntries.map((row) => (row === previous ? entry : row))
        : [...state.storyRunEntries, entry],
      storyPlanSnapshot:
        ownRun && snapshot && !state.storyPlanProposal
          ? {
              ...snapshot,
              stories: {
                ...snapshot.stories,
                entries: snapshot.stories.entries.map((story) =>
                  story.story_id === frame.storyId
                    ? { ...story, state: entry.state, commit_id: entry.commitId }
                    : story
                ),
              },
            }
          : snapshot,
    },
    key,
    frame.runId
  )
}

/** How the conversation says each step of the adjustment ladder (D15): how serious it reads, what
 * happened, and what the maker does next; null for a repair (L0), which is not said at all. One
 * entry per level, so a level the harness adds fails the build until it is given words. */
const ADJUSTMENT_NOTICE_BY_LEVEL: Readonly<
  Record<
    HarnessAdjustmentLevel,
    {
      level: Extract<HarnessTranscriptEntry, { type: 'notice' }>['level']
      text: (toVersion: number | null) => string
      action?: string
    } | null
  >
> = {
  L0: null,
  L1: {
    level: 'info',
    text: (toVersion) =>
      `The agent adjusted the plan within what you approved${toVersion ? ` — now v${toVersion}` : ''}.`,
  },
  L2: {
    level: 'info',
    text: () => 'The agent proposes a change beyond what you approved.',
    action: 'Review the change on the plan to continue the run.',
  },
  L3: {
    level: 'info',
    text: () => 'The agent proposes changing what this plan is for.',
    action: 'Review the change on the plan and give it the go-ahead again to continue.',
  },
  L4: { level: 'error', text: () => 'The plan is blocked.' },
}

/** One adjustment, as the conversation says it: what the agent did or asks, why, and what it
 * touched. Null when it is not said. */
function adjustmentNotice(adjustment: HarnessPlanAdjustment): HarnessTranscriptEntry | null {
  const wording = ADJUSTMENT_NOTICE_BY_LEVEL[adjustment.level]

  if (!wording) {
    return null
  }

  const { level, text, action } = wording
  const details = [
    ...adjustment.changed.map((id) => `Revised ${id}`),
    ...adjustment.added.map((id) => `Added ${id}`),
    ...adjustment.removed.map((id) => `Removed ${id}`),
  ]

  return {
    type: 'notice',
    level,
    code: ADJUSTMENT_NOTICE,
    text: [text(adjustment.to_version), adjustment.reason].filter(Boolean).join(' '),
    ...(details.length && { details }),
    ...(action && { action }),
  }
}

/** Kept separate from the `error` *frame* path above: an error frame is the harness reporting
 * something on a healthy stream, whereas here the stream itself broke, so no `end` is coming and
 * the turn must be closed here. */
export function applyTurnFailure(
  state: HarnessSessionState,
  code: string,
  message: string
): HarnessSessionState {
  return withNotice(
    {
      ...state,
      runningTools: [],
      streamState: 'error',
      transcript: withSettledFiles(settleStreaming(state.transcript)),
    },
    'error',
    errorText(code, message),
    { code }
  )
}

export const initialHarnessRunState: HarnessRunState = {
  appVersionId: null,
  preview: null,
  executionBusy: false,
  executionError: null,
}

/** The harness handed off a run that names another conversation. */
const FOREIGN_HANDOFF = 'The harness handed off a run this session did not ask for.'

/**
 * A run's frames, folded the way a turn's are.
 *
 * One vocabulary, one fold: every frame goes through `applyFrame`, so the run's tokens and tool
 * rows reach the transcript, an `error` lands as a line in the conversation that asked for the
 * run, and `branch_ready` says which branch it went to. What is added on top is only what a run
 * has and a turn does not — so a frame added to the run stream is handled in one place, or in
 * neither, and never in one of two.
 *
 * `end` is the one frame not passed on, because a run is not a turn. The plan's interrupt is open
 * throughout — a plan is never approved, its go-ahead *is* the run — so `endTurn` would clear, on
 * the run's last frame, the interrupt the maker still needs when the run failed.
 */
export function applyRunFrame(
  state: HarnessRunFold,
  frame: HarnessFrame,
  at: number = Date.now()
): HarnessRunFold {
  return foldRunPreservingTurnReply(state, (withoutTurnReply) => {
    switch (frame.type) {
      // A handoff for another conversation is refused rather than folded: its line would have this
      // session claiming what another built, and the store must not follow it out of the version.
      case 'branch_ready':
        return isOwnHandoff(frame.handoff, withoutTurnReply)
          ? { ...foldFrame(withoutTurnReply, frame, at), executionBusy: false, preview: null }
          : { ...withoutTurnReply, executionBusy: false, executionError: FOREIGN_HANDOFF }

      // Both: the run keeps the failure with a Retry beside it, and the conversation keeps the
      // line, because a run that failed only on the record reads as a run that never happened.
      case 'error':
        return {
          ...foldFrame(withoutTurnReply, frame, at),
          executionBusy: false,
          executionError: frame.message,
        }

      case 'end':
        return settleRun(withoutTurnReply)

      default:
        return foldFrame(withoutTurnReply, frame, at)
    }
  })
}

/** Accept the review for the plan it was asked about. A replacement clears the safe failure;
 * a restart review keeps the failed run until the Run that restarts it. */
export function acceptExecutionPreview(
  state: HarnessRunFold,
  preview: HarnessExecutionPreview,
  reviewingRestart: boolean
): HarnessRunFold {
  const record = state.record

  return {
    ...state,
    preview,
    executionBusy: false,
    ...(record?.plan?.execution &&
      !reviewingRestart && {
        record: {
          ...record,
          plan: { ...record.plan, state: 'READY' as const, execution: null },
        },
      }),
  }
}

/** The run's stream broke, so no `end` is coming and the run is closed here — as with a turn, and
 * for the same reason: every run control is disabled behind `executionBusy`, and a flag nothing
 * will clear again leaves them that way for the life of the session. */
export function applyRunFailure(state: HarnessRunFold, message: string): HarnessRunFold {
  return foldRunPreservingTurnReply(state, (withoutTurnReply) => {
    const settled = settleRun({ ...withoutTurnReply, executionError: message })

    return { ...settled, ...withNotice(settled, 'error', message) }
  })
}

/** The harness buffers a turn's consecutive tokens until its next non-token frame. A run writes
 * beside that buffer, so its entries precede the whole reply in the saved history. Hold that
 * reply out of the run's fold and put it back last, still streaming: no run frame may split it,
 * consume its tokens or settle it. A run's own token entry is complete here, so it cannot consume
 * the turn's first token either. Without a turn in flight, the run folds as it always did. */
function foldRunPreservingTurnReply(
  state: HarnessRunFold,
  fold: (withoutTurnReply: HarnessRunFold) => HarnessRunFold
): HarnessRunFold {
  if (state.streamState !== 'streaming') {
    return fold(state)
  }

  const last = state.transcript.at(-1)
  const reply = last?.type === 'assistant' && last.streaming ? last : null
  const folded = fold({
    ...state,
    transcript: reply ? state.transcript.slice(0, -1) : state.transcript,
  })

  return {
    ...folded,
    streamState: state.streamState,
    transcript: [...settleStreaming(folded.transcript), ...(reply ? [reply] : [])],
  }
}

/** The run's stream was cut, but the run goes on harness-side: the line says so while the store
 * reads where it stands, and the run is busy so nothing starts a second one meanwhile. */
export function applyRunCut(state: HarnessRunFold): HarnessRunFold {
  return foldRunPreservingTurnReply(state, (withoutTurnReply) => {
    const settled = {
      ...withoutTurnReply,
      runningTools: [],
      transcript: settleStreaming(withoutTurnReply.transcript),
      executionBusy: true,
    }

    return { ...settled, ...withNotice(settled, 'info', 'Connection lost. Checking on the run…') }
  })
}

/** A handoff belongs to the conversation that asked for the run, wherever its page is now. */
export function isOwnHandoff(
  handoff: HarnessBranchHandoff,
  scope: { sessionId: string | null }
): boolean {
  return handoff.source_session_id === scope.sessionId
}

/** The turn's fold, carried over the run's own fields — `applyFrame` knows the transcript and
 * nothing about executions. */
function foldFrame(state: HarnessRunFold, frame: HarnessFrame, at: number): HarnessRunFold {
  return { ...state, ...applyFrame(state, frame, at) }
}

/** The run is over and whatever it left half-written is settled. The conversation's own state —
 * its interrupt, its stream state — is none of the run's business. */
function settleRun(state: HarnessRunFold): HarnessRunFold {
  return {
    ...state,
    runningTools: [],
    transcript: settleStreaming(state.transcript),
    executionBusy: false,
  }
}

/** Append a quiet line to the transcript. Four callers were spreading the state, the transcript
 * and the same entry shape by hand — three chances each to forget one. */
export function withNotice(
  state: HarnessSessionState,
  level: 'info' | 'error',
  text: string,
  { code, retryable }: { code?: string; retryable?: boolean } = {}
): HarnessSessionState {
  return {
    ...state,
    transcript: [
      ...state.transcript,
      { type: 'notice', level, text, ...(code && { code }), ...(retryable && { retryable }) },
    ],
  }
}

/** The maker stopped the turn. Not an error: it closes as finished with a quiet line saying so,
 * because a spinner left turning after a Cancel is the one outcome this must never produce. */
export function applyCancel(state: HarnessSessionState): HarnessSessionState {
  return withNotice(
    {
      ...state,
      runningTools: [],
      streamState: 'finished',
      transcript: withSettledFiles(settleStreaming(state.transcript)),
    },
    'info',
    'Stopped. Nothing was saved.'
  )
}

export function appendUserMessage(
  state: HarnessSessionState,
  text: string,
  at: number = Date.now(),
  files: readonly HarnessSentFile[] = [],
  comment?: HarnessTurnComment
): HarnessSessionState {
  return {
    ...state,
    runningTools: [],
    streamState: 'streaming',
    transcript: [
      ...state.transcript,
      { type: 'user', text, at, ...(files.length && { files }), ...(comment && { comment }) },
    ],
  }
}

/** The last message, when its turn never ran: what a retryable refusal sends again. */
export function notRunMessage(
  transcript: readonly HarnessTranscriptEntry[]
): Extract<HarnessTranscriptEntry, { type: 'user' }> | null {
  const last = transcript.findLast((entry) => entry.type === 'user')

  return last?.type === 'user' && last.notRun ? last : null
}

/** The transcript a Retry sends a not-run message again from: the message gives way, refusal and
 * all, but the run messages after it stay — the run went on while it waited, and the record keeps
 * them. */
export function withoutNotRunMessage(
  transcript: readonly HarnessTranscriptEntry[],
  message: HarnessTranscriptEntry
): readonly HarnessTranscriptEntry[] {
  const at = transcript.indexOf(message)

  return [
    ...transcript.slice(0, at),
    ...transcript.slice(at + 1).filter((entry) => entry.type === 'run'),
  ]
}

/** A transcript rebuilt from the session's record, with the message on screen whose turn never
 * ran put back where it was: after the record, but before the run messages that came after it on
 * screen, which the record holds too. Unless the record holds that message after all: a turn cut
 * after the harness took it is in the record, which then has its words once more than the screen
 * has before it. Words, not a count of messages: a decision is a message on screen and an answer
 * in the record, and another tab adds messages this one never showed. */
export function keepNotRunMessage(
  rebuilt: readonly HarnessTranscriptEntry[],
  onScreen: readonly HarnessTranscriptEntry[]
): readonly HarnessTranscriptEntry[] {
  const held = notRunMessage(onScreen)

  if (!held) {
    return rebuilt
  }

  const timesSaid = (transcript: readonly HarnessTranscriptEntry[]): number =>
    transcript.filter(
      (entry) => (entry.type === 'user' || entry.type === 'answer') && entry.text === held.text
    ).length

  if (timesSaid(rebuilt) >= timesSaid(onScreen)) {
    return rebuilt
  }

  const runsAfter = onScreen
    .slice(onScreen.indexOf(held) + 1)
    .filter((entry) => entry.type === 'run').length
  const rebuiltRuns = rebuilt.flatMap((entry, index) => (entry.type === 'run' ? [index] : []))
  const at = (runsAfter && rebuiltRuns.at(-runsAfter)) || rebuilt.length

  return [...rebuilt.slice(0, at), held, ...rebuilt.slice(at)]
}

/** The last message, not run: its turn never ran — refused as a whole, or lost before its first
 * frame. Its files stay on it, no longer count as sent and are no longer being read. */
export function markTurnNotRun(state: HarnessSessionState): HarnessSessionState {
  const last = state.transcript.findLastIndex((entry) => entry.type === 'user')

  return {
    ...state,
    transcript: withSettledFiles(
      state.transcript.map((entry, index) =>
        index === last && entry.type === 'user' ? { ...entry, notRun: true } : entry
      )
    ),
  }
}

/** The optimistic line of a comment turn the harness never took, so sending the comment again
 * does not leave it twice. Only a comment line goes: a composer line stays, with its error. */
export function dropUnsentComment(
  state: HarnessSessionState,
  at: number,
  text: string
): HarnessSessionState {
  const index = state.transcript.findLastIndex(
    (entry) => entry.type === 'user' && !!entry.comment && entry.at === at && entry.text === text
  )

  return index < 0
    ? state
    : { ...state, transcript: state.transcript.filter((_, position) => position !== index) }
}

/** What a run page's resource is called on a comment's chip once only its type is known. */
const RUN_PAGE_KIND: Readonly<Record<string, string>> = {
  'ui-flow': 'UI flow',
  'process': 'Process',
}

/**
 * The comment a stored user turn carried, read back from the `ui_context` the harness kept with
 * it: `hint` when the harness read it, `supplied` when it set it aside. A comment is a turn whose
 * context names a run and a UI component on a resource editor; an editor turn on a UI flow also
 * selects components, but never has a run.
 */
function restoredComment(data: Record<string, unknown>): HarnessTurnComment | undefined {
  const stored = data['ui_context']

  if (!isRecord(stored)) {
    return undefined
  }

  const contextRead = stored['status'] === 'available'
  const sent = contextRead ? stored['hint'] : stored['supplied']

  if (!isRecord(sent) || !isRecord(sent['run']) || !isRecord(sent['page'])) {
    return undefined
  }

  const selected = Array.isArray(sent['selection']) ? sent['selection'][0] : undefined

  if (
    sent['page']['type'] !== 'resource_editor' ||
    !isRecord(selected) ||
    selected['type'] !== 'ui-component'
  ) {
    return undefined
  }

  const componentType = asString(selected['component_type'])
  const resourceType = asString(sent['page']['resource_type'])

  return {
    component: componentType ? commentLabelFor(componentType) : 'Component',
    page: Object.hasOwn(RUN_PAGE_KIND, resourceType) ? RUN_PAGE_KIND[resourceType] : 'Run',
    contextRead,
  }
}

/** Record what the maker settled without writing, as echo lines under the turn that asked. An
 * entry without a subject was an option picked, numbered `Q1`, `Q2`, … in the order answered;
 * steering entries name their own subject. */
export function appendAnswers(
  state: HarnessSessionState,
  answers: readonly HarnessAnswerInput[],
  at: number = Date.now()
): HarnessSessionState {
  let numbered = state.questionsAnswered

  const entries = answers.map((answer): HarnessTranscriptEntry => ({
    type: 'answer',
    subject: answer.subject ?? `Q${++numbered}`,
    text: answer.text,
    ...(answer.question && { question: answer.question }),
    at,
  }))

  return {
    ...state,
    runningTools: [],
    streamState: 'streaming',
    questionsAnswered: numbered,
    transcript: [...state.transcript, ...entries],
  }
}

/** What the harness writes as the maker's turn when they answered an interrupt without a message. */
const DECISION_EVENT = /^Decision: (approve|reject|respond)$/

/**
 * Rebuild a session from the record the harness kept of it.
 *
 * The history is the same frames the tab once streamed, so it is replayed through `applyFrame`
 * rather than through a second reading of the vocabulary. Two things the fold cannot know come
 * from the record itself: which interrupt is *still* open (a replayed `plan_proposed` opened one that a
 * later `user` event answered), and where the session stands now (`status`).
 *
 * `interruptRecovered` is set: a restored interrupt must not move the view.
 */
export function restoreSession(
  record: HarnessSessionRecord,
  at: number = Date.now()
): HarnessSessionState {
  const start: HarnessSessionState = {
    ...initialHarnessSessionState,
    sessionId: record.session_id,
    record,
  }

  const state = record.events.reduce((current, event) => {
    if (event.event === 'user') {
      const text = String(event.data['text'] ?? '')
      const decision = DECISION_EVENT.exec(text)

      // A bare decision is what the maker pressed, not what they wrote. Which artefact was
      // confirmed is read from the interrupt the fold has open at that point.
      if (decision) {
        return appendAnswers(
          current,
          [{ subject: 'Decision', text: decisionEcho(decision[1], current) }],
          at
        )
      }

      const picked = pickedQuestion(current, text)

      return picked === null
        ? appendUserMessage(
            current,
            text,
            at,
            toHistoryFiles(event.data['files']),
            restoredComment(event.data)
          )
        : appendAnswers(current, [{ text, question: picked }], at)
    }

    const frame = frameFromEvent(event.event, event.data)

    // The record is authoritative; a replayed `lifecycle` is an earlier snapshot of it, and so is
    // a replayed `attachment` of the state the message's `files` carry now.
    if (!frame || frame.type === 'lifecycle' || frame.type === 'attachment') {
      return current
    }

    const folded = applyFrame(current, frame, at)

    // A stored token event is already the whole buffered message. Adjacent token events can
    // belong to the run and the turn beside it, and each holds its own place for a feedback id.
    return frame.type === 'token'
      ? { ...folded, transcript: settleStreaming(folded.transcript) }
      : folded
  }, start)

  const interrupt = record.interrupt

  const hydrated = reconcileSessionRecord({ ...state, storyPlanProposal: null }, record)
  const reviewingPlan =
    interrupt &&
    isPlanReviewType(interrupt.type) &&
    !isPlanProposalSettled(record, storyProposalIdentity(interrupt.payload, record.revision))
  const reviewSnapshot =
    reviewingPlan && planUsesStories(interrupt.payload)
      ? toStoryPlanSnapshot(interrupt.payload)
      : null

  return {
    ...hydrated,
    ...(reviewingPlan && {
      storyPlanSnapshot: reviewSnapshot,
      storyPlanProposal: storyProposalIdentity(interrupt.payload, record.revision),
    }),
    transcript: settleStreaming(state.transcript),
    interrupt,
    interruptRecovered: !!interrupt,
    // The fold's own transient flags belong to the turns that are over.
    briefRevisionPending: false,
    streamState: restoredStreamState(record, !!interrupt),
  }
}

/**
 * An option the maker clicked, restored as the numbered check-line the tab drew at the time.
 *
 * The harness saves a picked answer as the maker's turn like any other text — `pick()` sends the
 * option as the resume's `message`, and that message is what the record keeps — so the only thing
 * that tells the two apart is that this text is one of the answers the open question offered.
 * Without the test a picked chip comes back as a second "You" bubble, opening a turn of its own.
 */
function pickedQuestion(state: HarnessSessionState, text: string): string | null {
  if (state.interrupt?.type !== 'ask_user') {
    return null
  }

  const { question, options } = toQuestionDisplay(state.interrupt.payload)

  return options.includes(text) ? question : null
}

/**
 * What a bare decision reads as, in the lifecycle's own words.
 *
 * A `respond` is the one that loses something: staged steering travels as typed fields, the
 * harness renders them for the model and saves only `Decision: respond`, so the per-decision
 * lines the tab showed cannot be rebuilt from the record. The copy says what was done rather
 * than claiming to be the whole of it.
 */
function decisionEcho(decision: string, state: HarnessSessionState): string {
  switch (decision) {
    case 'approve':
      return state.interrupt?.type === 'brief_review' ? 'Gave the go-ahead.' : 'Approved.'
    case 'reject':
      return 'Rejected the proposal.'
    default:
      switch (state.interrupt?.type) {
        case 'brief_review':
          return 'Asked for changes to the description.'
        case 'plan_review':
          return 'Asked for changes to the plan.'
        default:
          return 'Answered.'
      }
  }
}

/** `running` is a turn still in flight under its lease — the only status that is — and the store
 * reattaches to it. A transcript without `end` need not be running. `waiting` with no interrupt
 * to show is the record disagreeing with itself, and reads as finished rather than as a wait
 * for nothing. */
function restoredStreamState(
  record: HarnessSessionRecord,
  hasInterrupt: boolean
): HarnessStreamState {
  switch (record.status) {
    case 'idle':
      return 'idle'
    case 'running':
      return 'streaming'
    case 'waiting':
      return hasInterrupt ? 'awaiting-answer' : 'finished'
    case 'blocked':
      return 'blocked'
    case 'interrupted':
      return 'interrupted'
    default:
      return hasInterrupt ? 'awaiting-answer' : 'finished'
  }
}

function storyProposalIdentity(
  payload: unknown,
  recordRevision: number
): NonNullable<HarnessSessionState['storyPlanProposal']> {
  const proposal = isRecord(payload) ? payload : {}
  return {
    planId: asString(proposal['plan_id']) || null,
    version: asNumber(proposal['plan_version'] ?? proposal['version'], 0) || null,
    recordRevision,
  }
}

function applyInterrupt(
  state: HarnessSessionState,
  interrupt: HarnessInterrupt,
  at: number
): HarnessSessionState {
  // A brief carries its own identity: `rev` is assigned by the harness, not the model.
  if (interrupt.type === 'brief_review') {
    // Read off the payload rather than through `toBriefDisplay`: only two fields are wanted here,
    // and normalising fifteen sections to get them would put the display layer underneath the
    // state layer — and run the whole mapper twice per interrupt, the store's `brief` being the other.
    const payload = isRecord(interrupt.payload) ? interrupt.payload : {}
    const title = asString(payload['title'])

    // Prefer the harness's `rev`, but never go backwards or stall: it currently mints a fresh
    // plan_id per revision instead of incrementing `rev` (its TD-7), so a genuinely revised brief
    // arrives claiming to be revision 1. The larger of the two is right either way.
    const revision = Math.max(asNumber(payload['rev'], 1), state.briefRevision + 1)
    const revisedOnNote = state.briefRevisionPending

    return {
      ...state,
      interrupt,
      latestBrief: interrupt.payload,
      briefRevision: revision,
      briefRevisionPending: false,
      briefRevisedOnNote: revisedOnNote,
      transcript: [
        ...settleStreaming(state.transcript),
        {
          type: 'artefact-marker',
          artefact: 'brief',
          revision,
          at,
          ...(revisedOnNote && { revisedOnNote: true }),
          ...(title && { title }),
        },
      ],
    }
  }

  // Every other interrupt — a question, a fix or ship review — leaves its mark in the transcript
  // rather than only in `interrupt`, so the turn it ended can be rated once it is no longer open.
  if (!isPlanReviewType(interrupt.type)) {
    return {
      ...state,
      interrupt,
      transcript: [...settleStreaming(state.transcript), { type: 'interrupt', at }],
    }
  }

  // A plan is always a revision of the session's plan, never a second one. `plan_id` cannot decide
  // that — the captured frame carried none — so position does: any later `plan_proposed` supersedes
  // the last. The plan view only ever shows the latest; plans live in the checkpoint.
  const revision = state.planRevision + 1

  return {
    ...state,
    interrupt,
    latestPlan: interrupt.payload,
    storyPlanSnapshot: planUsesStories(interrupt.payload)
      ? toStoryPlanSnapshot(interrupt.payload)
      : null,
    storyPlanProposal: storyProposalIdentity(interrupt.payload, state.record?.revision ?? 0),
    planRevision: revision,
    transcript: [
      ...settleStreaming(state.transcript),
      // Every plan after the first is a revision on the maker's word.
      {
        type: 'artefact-marker',
        artefact: 'plan',
        revision,
        at,
        ...(revision > 1 && { revisedOnNote: true }),
      },
    ],
  }
}

/** Close the open interrupt without answering it — the harness withdrew it. */
function settleInterrupt(state: HarnessSessionState): HarnessSessionState {
  return { ...state, interrupt: null }
}

export const CANCEL_NOT_STOPPED_NOTICE = 'cancel_not_stopped'

function endTurn(state: HarnessSessionState, paused: boolean): HarnessSessionState {
  const transcript = withSettledFiles(settleStreaming(state.transcript)).filter(
    (entry) => !(entry.type === 'notice' && entry.code === CANCEL_NOT_STOPPED_NOTICE)
  )

  if (!paused) {
    // A `planning_failed` or an `interrupted` already said where this turn stands; `end` confirms it.
    return {
      ...state,
      runningTools: [],
      transcript,
      streamState:
        state.streamState === 'blocked' || state.streamState === 'interrupted'
          ? state.streamState
          : 'finished',
      interrupt: null,
    }
  }

  // `paused` promises a decision is outstanding, so a missing interrupt means we were told to wait
  // for something we cannot show. Saying so beats rendering an interface with nothing in it.
  if (!state.interrupt) {
    return withNotice(
      { ...state, runningTools: [], streamState: 'error', transcript },
      'error',
      'The harness paused for a decision but did not say what it is asking.'
    )
  }

  return { ...state, runningTools: [], transcript, streamState: 'awaiting-answer' }
}

/** Tokens accumulate into the trailing assistant entry, so streaming text renders as one growing
 * message rather than one entry per token. */
function appendToken(
  transcript: readonly HarnessTranscriptEntry[],
  text: string,
  at: number
): HarnessTranscriptEntry[] {
  const last = transcript[transcript.length - 1]

  return last?.type === 'assistant' && last.streaming
    ? [...transcript.slice(0, -1), { ...last, text: last.text + text }]
    : [...transcript, { type: 'assistant', text, streaming: true, at }]
}

/** Close any open assistant entry — the text is final once something else happens. */
function settleStreaming(transcript: readonly HarnessTranscriptEntry[]): HarnessTranscriptEntry[] {
  return transcript.map((entry) =>
    entry.type === 'assistant' && entry.streaming ? { ...entry, streaming: false } : entry
  )
}

/** The tool whose result is planning stopping, not a call failing. */
const BLOCKED_TOOL = 'planning_blocked'

function removeRunningTool(
  runningTools: HarnessSessionState['runningTools'],
  toolUseId: string
): HarnessSessionState['runningTools'] {
  const index = runningTools.findIndex((tool) => tool.toolUseId === toolUseId)

  return index === -1
    ? runningTools
    : [...runningTools.slice(0, index), ...runningTools.slice(index + 1)]
}

function resolveTool(
  transcript: readonly HarnessTranscriptEntry[],
  frame: Extract<HarnessFrame, { type: 'tool_use_result' }>,
  at: number
): HarnessTranscriptEntry[] {
  const outcome =
    frame.name === BLOCKED_TOOL
      ? {
          status: 'blocked' as const,
          summary: 'Planning stopped here; the reason follows.',
        }
      : readToolOutcome(frame.content)

  // A result lands on the call still in flight under its id, and only on a RUNNING row: the interrupt
  // check reuses one id for the whole conversation, so matching any row with that id had the second
  // turn's check rewriting the first turn's row. A result with no start to land on is still work
  // the agent did — the interrupt check arrives exactly so — and becomes a row of its own.
  const inFlight = transcript.findIndex(
    (entry) =>
      entry.type === 'tool' && entry.toolUseId === frame.toolUseId && entry.status === 'running'
  )

  if (inFlight === -1) {
    return [
      ...transcript,
      {
        type: 'tool',
        toolUseId: frame.toolUseId,
        name: frame.label || frame.name,
        isRawName: !frame.label,
        status: outcome.status,
        resultSummary: outcome.summary,
        at,
      },
    ]
  }

  return transcript.map((entry, index) =>
    index === inFlight && entry.type === 'tool'
      ? {
          ...entry,
          // The result frame carries the label too; prefer it if the start frame had none.
          ...(frame.label && entry.isRawName ? { name: frame.label, isRawName: false } : {}),
          status: outcome.status,
          resultSummary: outcome.summary,
        }
      : entry
  )
}

/**
 * What a tool result actually says. `content` is a JSON-encoded string rather than an object, and
 * is not guaranteed to be JSON at all. A recoverable failure — the agent trying a resource id it
 * could not obtain, say — arrives here as an ordinary result, NOT as an error frame, and must not
 * read as the turn having failed: it is the agent working, not the stream breaking.
 *
 * A saved history carries the outcome instead of the result (`"Result received; see the trace for
 * details."`, or `{recoverable, message}`), and both read correctly here without a special case.
 */
function readToolOutcome(content: unknown): { status: HarnessToolStatus; summary: string } {
  const parsed = typeof content === 'string' ? tryParseJson(content) : content

  if (parsed && typeof parsed === 'object') {
    const record = parsed as Record<string, unknown>
    // A KEY named `error` is not a failure: `content` is whatever the tool returned, and a process
    // definition carrying `error: null` turned the row amber. The summary line below already
    // assumes a truthy `error`, which is the tell.
    const failed = !!record['error'] || record['recoverable'] === true

    if (failed) {
      return {
        status: 'recoverable-error',
        summary: String(record['message'] ?? record['error'] ?? 'The tool call did not succeed.'),
      }
    }
  }

  return { status: 'done', summary: summarize(parsed ?? content) }
}

const SUMMARY_LIMIT = 200

function summarize(value: unknown): string {
  const text = typeof value === 'string' ? value : compactJson(value)

  return text.length > SUMMARY_LIMIT ? `${text.slice(0, SUMMARY_LIMIT)}…` : text
}

function withLastTurnAttachment(
  transcript: readonly HarnessTranscriptEntry[],
  status: HarnessAttachmentStatus
): readonly HarnessTranscriptEntry[] {
  const last = transcript.findLastIndex((entry) => entry.type === 'user')

  return transcript.map((entry, index) =>
    index === last && entry.type === 'user' && entry.files
      ? { ...entry, files: withAttachmentStatus(entry.files, status) }
      : entry
  )
}

/** Copy that distinguishes the maker's problem from the operator's. `no_checkpointer` in
 * particular is a misconfigured service, not a failed request. */
function errorText(code: string, message: string): string {
  switch (code) {
    case HARNESS_ERROR_CODES.budgetExceeded:
      return `Daily limit reached — ${message}`

    case HARNESS_ERROR_CODES.noCheckpointer:
      return `The harness is running without a checkpointer, so it cannot pause for decisions. This is a service configuration problem, not a problem with your request. (${message})`

    case HARNESS_ERROR_CODES.noPendingInterrupt:
      return 'This session is no longer waiting on that decision — it may have been answered elsewhere, or the harness restarted. Nothing was applied.'

    case HARNESS_ERROR_CODES.streamTruncated:
      // Blames the connection rather than the harness on purpose: measurement put the fault in
      // the transport — clean every time straight to the harness, cut every time through the
      // relay. The harness records how the turn actually ended, which is why the copy says to
      // reload.
      return 'The connection ended before the turn finished, so the rest of it did not reach you. Reload to see how the harness recorded it.'

    case HARNESS_ERROR_CODES.notAuthenticated:
      return 'Your session has expired. Log in again to continue.'

    case HARNESS_ERROR_CODES.malformedResponse:
      // Deliberately not "try again": the same request will return the same body. This is a
      // version skew between the designer and the harness, which is an operator's problem.
      return 'The harness answered with something this version of the designer cannot read. This is a service mismatch, not a problem with your request.'

    default:
      return message
  }
}
